import React, { useState, useEffect } from 'react';
import { ContractClient } from '../contract';
import { ReadOnlyContractClient } from '../contract-readonly';

interface KYCVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userAddress: string;
  countryCode: string;
  countryName: string;
  contractClient: ContractClient | null;
  readOnlyClient: ReadOnlyContractClient;
  onKYCVerified?: () => void;
}

const KYCVerificationModal: React.FC<KYCVerificationModalProps> = ({
  isOpen,
  onClose,
  userAddress,
  countryCode,
  countryName,
  contractClient,
  readOnlyClient,
  onKYCVerified
}) => {
  const [kycData, setKycData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadKYCData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await readOnlyClient.getKYCData(userAddress, countryCode);
      if (data) {
        setKycData(data);
      } else {
        setKycData(null);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load KYC data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && userAddress && countryCode) {
      loadKYCData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userAddress, countryCode]);

  const handleVerify = async (status: 'approved' | 'rejected') => {
    if (!contractClient) {
      setError('Contract client not available');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      await contractClient.verifyKYCData(userAddress, countryCode, status);
      alert(`KYC ${status} successfully!`);
      if (onKYCVerified) {
        onKYCVerified();
      }
      await loadKYCData(); // Reload to show updated status
    } catch (error: any) {
      setError(error.message || `Failed to ${status} KYC`);
    } finally {
      setIsVerifying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflowY: 'auto',
        width: '90%',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>KYC Verification - {countryName}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
          <strong>User Address:</strong> {userAddress}
        </div>

        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px'
          }}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            Loading KYC data...
          </div>
        ) : kycData ? (
          <>
            <div style={{
              padding: '12px',
              marginBottom: '20px',
              backgroundColor: kycData.kycStatus === 'approved' ? '#d4edda' : kycData.kycStatus === 'rejected' ? '#f8d7da' : '#fff3cd',
              borderRadius: '4px',
              border: `1px solid ${kycData.kycStatus === 'approved' ? '#c3e6cb' : kycData.kycStatus === 'rejected' ? '#f5c6cb' : '#ffeaa7'}`
            }}>
              <strong>Current Status:</strong> {kycData.kycStatus.toUpperCase()}
              {kycData.kycVerifiedAt && (
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  Verified: {new Date(kycData.kycVerifiedAt * 1000).toLocaleString()}
                </div>
              )}
              {kycData.kycVerifiedBy && (
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  Verified By: {kycData.kycVerifiedBy}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '12px', fontSize: '18px' }}>KYC Information</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                {kycData.givenName && (
                  <div>
                    <strong>Given Name:</strong> {kycData.givenName}
                  </div>
                )}
                {kycData.familyName && (
                  <div>
                    <strong>Family Name:</strong> {kycData.familyName}
                  </div>
                )}
                {kycData.emailAddress && (
                  <div>
                    <strong>Email:</strong> {kycData.emailAddress}
                  </div>
                )}
                {kycData.mobileNumber && (
                  <div>
                    <strong>Mobile:</strong> {kycData.mobileNumber}
                  </div>
                )}
                {kycData.birthDate && (
                  <div>
                    <strong>Date of Birth:</strong> {kycData.birthDate}
                  </div>
                )}
                {kycData.birthPlace && (
                  <div>
                    <strong>Place of Birth:</strong> {kycData.birthPlace}
                  </div>
                )}
                {kycData.address && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <strong>Address:</strong> {kycData.address}
                  </div>
                )}
                {kycData.city && (
                  <div>
                    <strong>City:</strong> {kycData.city}
                  </div>
                )}
                {kycData.stateOrProvince && (
                  <div>
                    <strong>State/Province:</strong> {kycData.stateOrProvince}
                  </div>
                )}
                {kycData.postalCode && (
                  <div>
                    <strong>Postal Code:</strong> {kycData.postalCode}
                  </div>
                )}
                {kycData.idType && (
                  <div>
                    <strong>ID Type:</strong> {kycData.idType}
                  </div>
                )}
                {kycData.idNumber && (
                  <div>
                    <strong>ID Number:</strong> {kycData.idNumber}
                  </div>
                )}
                {kycData.taxId && (
                  <div>
                    <strong>Tax ID:</strong> {kycData.taxId}
                  </div>
                )}
                {kycData.occupation && (
                  <div>
                    <strong>Occupation (ISCO08):</strong> {kycData.occupation}
                  </div>
                )}
                {kycData.employerName && (
                  <div>
                    <strong>Employer:</strong> {kycData.employerName}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => handleVerify('approved')}
                disabled={isVerifying || kycData.kycStatus === 'approved'}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: isVerifying || kycData.kycStatus === 'approved' ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isVerifying || kycData.kycStatus === 'approved' ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                {isVerifying ? 'Verifying...' : 'Approve KYC'}
              </button>
              <button
                onClick={() => handleVerify('rejected')}
                disabled={isVerifying || kycData.kycStatus === 'rejected'}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: isVerifying || kycData.kycStatus === 'rejected' ? '#ccc' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isVerifying || kycData.kycStatus === 'rejected' ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                {isVerifying ? 'Verifying...' : 'Reject KYC'}
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No KYC data found for this user in {countryName}.</p>
            <button
              onClick={onClose}
              style={{
                padding: '12px 24px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                marginTop: '20px'
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYCVerificationModal;
